import React, { useEffect, useState } from 'react';
import { UnscheduledEvent } from '../../types';
import { SKYDENT_FAVICON_URL, EVENT_STATUS } from '../../constants';
import {
  DAMAGE_CODE_ENTRIES,
  DamageCodeEntry,
  DamageCodeOption,
  isDamageCodeGroup,
} from '../../data/damage-codes';
import { refreshSkyDentDamageStatus } from '../../services/damageLocationStatus';
import { useSkyDentDamageStatus } from '../../hooks/useDamageLocationStatus';
import SkyDentModal from '../SkyDentModal';

/**
 * Props for the damage code dropdown.
 */
interface DamageCodeSelectProps {
  damageCode: string;
  setDamageCode: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * Damage code dropdown, rendered from {@link DAMAGE_CODE_ENTRIES}. Highlights as
 * a missing required field until a code is chosen.
 */
const DamageCodeSelect: React.FC<DamageCodeSelectProps> = ({ damageCode, setDamageCode }) => (
  <select
    className={`field-input damage-code-select${!damageCode ? ' field-required-missing' : ''}`}
    value={damageCode}
    onChange={(e) => setDamageCode(e.target.value)}
    required
  >
    <option value="">Damage Code...</option>
    {DAMAGE_CODE_ENTRIES.map((entry: DamageCodeEntry) =>
      isDamageCodeGroup(entry) ? (
        <optgroup key={entry.label} label={entry.label}>
          {entry.options.map((option: DamageCodeOption) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ) : (
        <option key={entry.value} value={entry.value}>
          {entry.label}
        </option>
      ),
    )}
  </select>
);

/**
 * Props for the IropsSection component.
 */
interface IropsSectionProps {
  formData: UnscheduledEvent;
  isReadOnly: boolean;
  damageCode: string;
  setDamageCode: React.Dispatch<React.SetStateAction<string>>;
  updateField: (field: keyof UnscheduledEvent, value: string | number | boolean) => void;
  /** Re-runs flight auto-populate; `iropSelected` applies the RTB/ATO/ATB mapping. */
  populateFlights?: (tail: string, iropSelected: boolean) => void;
  isPlanned?: boolean;
}

/**
 * IROPS checkboxes section (RTB, ATO, ATB, DIV, CXL, Delay, Damage) with damage code dropdown.
 * When Damage is combined with Delay or CXL and a damage code is chosen, a
 * Chargeable / Non-Chargeable slide toggle is shown (defaults to Non-Chargeable).
 */
const IropsSection: React.FC<IropsSectionProps> = ({
  formData,
  isReadOnly,
  damageCode,
  setDamageCode,
  updateField,
  populateFlights,
  isPlanned,
}) => {
  // Show the chargeable toggle only when Damage co-occurs with Delay or CXL and
  // a specific damage code has been selected.
  const showChargeableToggle =
    !!formData.damage && (!!formData.delay || !!formData.cancellation) && !!damageCode;
  const isChargeable = !!formData.chargeable;

  // The SkyDent affordance appears only once SkyDent has ingested this damage
  // event (i.e. a correlating SkyDent record exists) — not immediately on
  // choosing a damage code. Existence is checked by event id, so it never shows
  // for an unsaved (create-mode) event. Clicking it opens the SkyDent edit
  // window inside Aether rather than the external site.
  const eventId = formData.eventId;
  const damageWithCode = !!formData.damage && !!damageCode;
  const [isSkyDentModalOpen, setIsSkyDentModalOpen] = useState<boolean>(false);

  const skyDentStatus = useSkyDentDamageStatus(eventId, damageWithCode && !!eventId);
  // Fetch fresh SkyDent status when the section opens for a damage event, so the
  // affordance reflects current reality rather than an earlier shared-store entry
  // (e.g. cached before SkyDent had ingested the record).
  useEffect(() => {
    if (damageWithCode && eventId) refreshSkyDentDamageStatus(eventId);
  }, [eventId, damageWithCode]);
  const showSkyDentLink = damageWithCode && skyDentStatus?.exists === true;
  // Red-box the SkyDent icon while the (SkyDent-owned) Damage Location is blank.
  const damageLocationMissing = skyDentStatus?.locationMissing === true;
  // A Completed damage record requires the Damage Location, so the SkyDent modal
  // highlights it as a required field in that case.
  const requireDamageLocation = formData.status === EVENT_STATUS.COMPLETED && !!formData.damage;

  return (
    <div className="field-row irops-row">
      <label className="field-label field-label-vcenter">IROPS</label>
      <div className="field-group irops-group">
        {!isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={formData.returnToBlock}
              disabled={isReadOnly}
              onChange={(e) => {
                updateField('returnToBlock', e.target.checked);
                populateFlights?.(
                  formData.tailNumber,
                  e.target.checked ||
                    !!formData.abortedTakeOff ||
                    !!formData.airTurnBack ||
                    !!formData.diversion,
                );
              }}
            />
            RTB
          </label>
        )}
        {!isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={formData.abortedTakeOff}
              disabled={isReadOnly}
              onChange={(e) => {
                updateField('abortedTakeOff', e.target.checked);
                populateFlights?.(
                  formData.tailNumber,
                  !!formData.returnToBlock ||
                    e.target.checked ||
                    !!formData.airTurnBack ||
                    !!formData.diversion,
                );
              }}
            />
            ATO
          </label>
        )}
        {!isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={formData.airTurnBack}
              disabled={isReadOnly}
              onChange={(e) => {
                updateField('airTurnBack', e.target.checked);
                populateFlights?.(
                  formData.tailNumber,
                  !!formData.returnToBlock ||
                    !!formData.abortedTakeOff ||
                    e.target.checked ||
                    !!formData.diversion,
                );
              }}
            />
            ATB
          </label>
        )}
        {!isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={formData.diversion}
              disabled={isReadOnly}
              onChange={(e) => {
                updateField('diversion', e.target.checked);
                populateFlights?.(
                  formData.tailNumber,
                  !!formData.returnToBlock ||
                    !!formData.abortedTakeOff ||
                    !!formData.airTurnBack ||
                    e.target.checked,
                );
              }}
            />
            DIV
          </label>
        )}
        {!isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={formData.cancellation}
              disabled={isReadOnly}
              onChange={(e) => updateField('cancellation', e.target.checked)}
            />
            CXL
          </label>
        )}
        {!isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={formData.delay}
              disabled={isReadOnly}
              onChange={(e) => {
                updateField('delay', e.target.checked);
                if (!e.target.checked) updateField('delayTimeMinutes', 0);
              }}
            />
            Delay
          </label>
        )}
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={formData.damage}
            disabled={isReadOnly}
            onChange={(e) => {
              updateField('damage', e.target.checked);
              if (!e.target.checked) setDamageCode('');
            }}
          />
          Damage
        </label>
        {formData.damage && !isReadOnly && (
          <DamageCodeSelect damageCode={damageCode} setDamageCode={setDamageCode} />
        )}
        {formData.damage && isReadOnly && damageCode && (
          <span className="damage-code-display">{damageCode}</span>
        )}
        {showSkyDentLink && (
          <button
            type="button"
            className={`skydent-link-btn${damageLocationMissing ? ' skydent-required' : ''}`}
            onClick={() => setIsSkyDentModalOpen(true)}
            aria-label="Open the SkyDent damage record"
            title={
              damageLocationMissing
                ? 'SkyDent Damage Location required'
                : 'Edit SkyDent damage record'
            }
          >
            <img className="skydent-favicon" src={SKYDENT_FAVICON_URL} alt="" aria-hidden="true" />
          </button>
        )}
        {isSkyDentModalOpen && (
          <SkyDentModal
            eventId={eventId}
            isOpen={isSkyDentModalOpen}
            onClose={() => setIsSkyDentModalOpen(false)}
            onSaved={() => refreshSkyDentDamageStatus(eventId)}
            requireDamageLocation={requireDamageLocation}
            readOnly={isReadOnly}
          />
        )}
        {showChargeableToggle && (
          <label className={`chargeable-toggle ${isChargeable ? 'chargeable-on' : ''}`}>
            <input
              type="checkbox"
              className="chargeable-toggle-input"
              checked={isChargeable}
              disabled={isReadOnly}
              onChange={(e) => updateField('chargeable', e.target.checked)}
            />
            <span className="chargeable-toggle-slider" aria-hidden="true" />
            <span className="chargeable-toggle-label">
              {isChargeable ? 'Chargeable' : 'Non-Chargeable'}
            </span>
          </label>
        )}
        {isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.longTerm}
              disabled={isReadOnly}
              onChange={(e) => updateField('longTerm', e.target.checked)}
            />
            Long Term
          </label>
        )}
        {isPlanned && (
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={!!formData.weekendSit}
              disabled={isReadOnly}
              onChange={(e) => updateField('weekendSit', e.target.checked)}
            />
            Weekend Sit
          </label>
        )}
      </div>
    </div>
  );
};

export default IropsSection;
